"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.HttpAdapter = void 0;
const Constants_1 = require("../Constants");
const AlertAdapter_1 = require("./alerts/AlertAdapter");
const ChatAdapter_1 = require("./chats/ChatAdapter");
const CommonAdapter_1 = require("./common/CommonAdapter");
const MessageAdapter_1 = require("./message/MessageAdapter");
const OrganizationAdapter_1 = require("./organization/OrganizationAdapter");
const StarredMessageAdapter_1 = require("./starredMessages/StarredMessageAdapter");
const UserDirectoryAdapter_1 = require("./user/UserDirectoryAdapter");
const OfflineRequestInterceptor_1 = require("./interceptors/OfflineRequestInterceptor");
const AuthIntereceptor_1 = require("./interceptors/AuthIntereceptor");
const DuplicateRequestInterceptor_1 = require("./interceptors/DuplicateRequestInterceptor");
const DuplicateResponseInterceptor_1 = require("./interceptors/DuplicateResponseInterceptor");
const AuthResponseInterceptor_1 = require("./interceptors/AuthResponseInterceptor");
const LabsAdapter_1 = require("./labs/LabsAdapter");
const OfflineResponseInterceptor_1 = require("./interceptors/OfflineResponseInterceptor");
const EMRAdapter_1 = require("./emr/EMRAdapter");
const SyncQueueAdapter_1 = require("./syncQueue/SyncQueueAdapter");
/* eslint-disable no-useless-catch */
class HttpAdapter {
    constructor(accessTokenProvider, config, axios, authStatusManager, duplicateRequestDetector, internetStatusManager, organizationId) {
        var _a;
        this.accessTokenProvider = accessTokenProvider;
        this.authStatusManager = authStatusManager;
        this.config = config;
        this.internetStatusManager = internetStatusManager;
        this.duplicateRequestDetector = duplicateRequestDetector;
        this.axiosInst = axios.create({
            baseURL: config.baseUrl,
            headers: Object.assign({ [Constants_1.ApiHeader.CONTENT_TYPE]: "application/json" }, (organizationId && {
                [Constants_1.ApiHeader.ORGANIZATION_ID]: organizationId,
            })),
        });
        new OfflineRequestInterceptor_1.OfflineRequestInterceptor(this.internetStatusManager, axios).register(this.axiosInst);
        new AuthIntereceptor_1.AuthInterceptor(this.accessTokenProvider, this.authStatusManager, axios).register(this.axiosInst);
        // IMPORTANT: This duplicate request interceptor must be registered to run FIRST.
        // If other request interceptors are registered to run before this one and they cancel
        // a request, the duplicate response interceptor may fail while cleaning such cancelled
        // requests.
        new DuplicateRequestInterceptor_1.DuplicateRequestInterceptor(this.duplicateRequestDetector, axios).register(this.axiosInst);
        new DuplicateResponseInterceptor_1.DuplicateResponseInterceptor(this.duplicateRequestDetector, axios).register(this.axiosInst);
        new AuthResponseInterceptor_1.AuthResponseInterceptor(this.authStatusManager, axios).register(this.axiosInst);
        new OfflineResponseInterceptor_1.OfflineResponseInterceptor(this.internetStatusManager, axios).register(this.axiosInst);
        this.axiosInst.defaults.headers[Constants_1.ApiHeader.DEVICE_TYPE] =
            config.platformType;
        this.axiosInst.defaults.headers[Constants_1.ApiHeader.APPLICATION_VERSION] =
            config.applicationVersion;
        this.axiosInst.defaults.headers[Constants_1.ApiHeader.API_VERSION] = config.apiVersion;
        this.organizationAdapter = new OrganizationAdapter_1.OrganizationAdapter(this, this.config);
        this.chatAdapter = new ChatAdapter_1.ChatAdapter(this, this.config);
        this.messageAdapter = new MessageAdapter_1.MessageAdapter(this, this.config);
        this.userDirectoryAdapter = new UserDirectoryAdapter_1.UserDirectoryAdapter(this, this.config);
        this.commonAdapter = new CommonAdapter_1.CommonAdapter(this, this.config);
        this.alertAdapter = new AlertAdapter_1.AlertAdapter(this, this.config);
        this.starredMessageAdapter = new StarredMessageAdapter_1.StarredMessageAdapter(this, this.config);
        this.labsAdapter = new LabsAdapter_1.LabsAdapter(this, this.config);
        this.syncQueueAdapter = new SyncQueueAdapter_1.SyncQueueAdapter(this, this.config);
        const isEmrEnabledOrg = !!organizationId &&
            !!((_a = this.config.emrEnabledOrgs) === null || _a === void 0 ? void 0 : _a.includes(organizationId));
        const emrBaseUrl = this.config.emrReportingHost && isEmrEnabledOrg
            ? `${this.config.emrReportingHost}${organizationId}/patients/`
            : "";
        this.emrAdapter = new EMRAdapter_1.EMRAdapter(this, emrBaseUrl, this.config.env);
    }
    uploadFile(url, stream, contentSize) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.axiosInst.put(url, stream, {
                headers: {
                    "Content-Type": "application/octet-stream",
                    "Content-Length": contentSize,
                },
            });
        });
    }
    get(url, options) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                return yield this.axiosInst.get(url, options);
            }
            catch (e) {
                console.log("Error in GET request:", e);
                throw e;
            }
        });
    }
    post(url, data, options) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                return yield this.axiosInst.post(url, data, options);
            }
            catch (e) {
                console.log("Error in post request:", e);
                throw e;
            }
        });
    }
    put(url, data, options) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                return yield this.axiosInst.put(url, data, options);
            }
            catch (e) {
                console.log("Error in put request:", e);
                throw e;
            }
        });
    }
    delete(url, options) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                return yield this.axiosInst.delete(url, options);
            }
            catch (e) {
                throw e;
            }
        });
    }
    patch(url, data, options) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                return yield this.axiosInst.patch(url, data, options);
            }
            catch (e) {
                throw e;
            }
        });
    }
    getLabsAdapter() {
        return this.labsAdapter;
    }
    getOrganizationAdapter() {
        return this.organizationAdapter;
    }
    getChatAdapter() {
        return this.chatAdapter;
    }
    getMessageAdapter() {
        return this.messageAdapter;
    }
    getUserDirectoryAdapter() {
        return this.userDirectoryAdapter;
    }
    getCommonAdapter() {
        return this.commonAdapter;
    }
    getAlertAdapter() {
        return this.alertAdapter;
    }
    getStarredMessageAdapter() {
        return this.starredMessageAdapter;
    }
    getEMRAdapter() {
        return this.emrAdapter;
    }
    getSyncQueueAdapter() {
        return this.syncQueueAdapter;
    }
}
exports.HttpAdapter = HttpAdapter;
